import styled from "@emotion/styled"
import { Theme, Typography, useMediaQuery, useTheme } from "@mui/material"
import BlossmLogo from "../assets/BlossmLogoWhite.svg"
import { Colors } from "../../styles/Colors"
import Link from "@mui/material/Link"
import React from "react"

export const Privacy = (): JSX.Element => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"))

  return (
    <StyledPrivacy theme={theme} isMobile={isMobile} isTablet={isTablet}>
      <div className={"app-bar"}>
        <div className={"privacy-header"}>
          <Link href={"/login"}>
            <img src={BlossmLogo.src} className={"header-icon"} />
          </Link>
        </div>
        <div className={"text-float"}>
          <Typography variant={"h4"} sx={{ mb: 1, mt: 1 }} fontWeight={"bold"}>
            PRIVACY POLICY
          </Typography>
        </div>
      </div>
      <div className={"privacy-body"}>
        <Typography variant={"body1"} fontStyle={"italic"}>
          Last Modified: July 28, 2023
        </Typography>
        <Typography variant={"body1"}>
          This Privacy Policy (the “<strong>Policy</strong>”) applies to your
          use of the Blossm website located at https://www.blossm.com (the “
          <strong>Website</strong>”) and the Blossm browser extension (the “
          <strong>Software</strong>”), owned and operated by Blossm Media, LLC
          (“
          <strong>Blossm</strong>,” “<strong>we</strong>,” “<strong>our</strong>
          ,” or “<strong>us</strong>”). This Policy applies to all visitors,
          customers, and users of the Website and Software and covers how we
          collect, use, store, and disclose information about you when you use
          the Website and Software. Please read the Policy carefully, as your
          use of the Website and Software constitutes your agreement with the
          Policy. If you do not agree with the Policy, do not use the Website
          and Software.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Minors
        </Typography>
        <Typography variant={"body1"}>
          The Website was not developed or intended for minors under 18 years of
          age (“<strong>Minors</strong>”). We do not knowingly collect
          information from Minors. Minors may not use the Website. If you use
          the Website, Software, or our services, you represent that you are at
          least 18 years of age. If you are the parent or guardian of a Minor
          and learn that he/she has provided us with personal information, you
          may contact us at privacy@blossm.com, and we will act to delete such
          information expeditiously.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Information We Collect
        </Typography>
        <Typography variant={"body1"}>
          To provide our services and assure the Website and Software is
          functioning properly, it is necessary for us to collect certain
          information, including personal information (i.e., information that
          could identify you). Information we collect may include:
        </Typography>
        <Typography variant={"body1"} fontWeight={"bold"}>
          Information You Provide
        </Typography>
        <div className={"list-body"}>
          <Typography variant={"body1"}>
            We may collect information that you provide to us or participating
            Influencers, including when you use the Website or Software, create
            a customer account, request services or support, or contact us in
            any way.
          </Typography>
          <ul className={"list"}>
            <li>
              <strong>Customer Account.</strong> To request services, we will
              require you to sign up for a customer account. If you sign up for
              a customer account, we will collect certain personal information,
              which may include your name, e-mail address, and payment and
              billing information. You may update certain information through
              your customer account on the Website.
            </li>
            <li>
              <strong>Request Services.</strong> To request services, we will
              need to collect additional information, which may include your
              social media handle(s), photograph(s), and the date and time of
              the requested services.
            </li>
            <li>
              <strong>Support, Inquiries, and Messages.</strong> If you contact
              us or participating Influencers, we may keep a complete log of all
              communications.
            </li>
            <li>
              <strong>Software. </strong> Influencers may install the Software
              to provide certain subscription-based social media account
              statistics, including from an Influencer’s OnlyFans account. If an
              Influencer installs the Software, we may automatically and
              periodically collect certain personal information, for example,
              the Influencer’s OnlyFans username, as well as other related
              non-personal account information including, but not limited to,
              top percentage, fans count, and following count (collectively, the
              “<strong>Metrics</strong>”).
            </li>
          </ul>
        </div>
        <Typography variant={"body1"} fontWeight={"bold"}>
          Information Collected Automatically
        </Typography>
        <Typography variant={"body1"}>
          We automatically collect the following information when you visit the
          Website or use the Software: IP address, certain device hardware and
          software information, browser information, Website usage and access
          time, and other analytical information, through the use of cookies, as
          described below.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          How We Use Collected Information
        </Typography>
        <div className={"list-body"}>
          <ul className={"list"}>
            <li>
              <strong>Services.</strong> We may use your information in
              connection with providing and performing our services and for
              other related business purposes necessary in our legitimate
              interests.
            </li>
            <li>
              <strong>Website.</strong> We may use your information in
              connection with operating, maintaining, and improving our Website.
            </li>
            <li>
              <strong>Software.</strong> We may use your information in
              connection with operating, maintaining, and improving our
              Software, including providing updates to same. Any Metrics
              transferred to us from the Software will adhere to the Chrome Web
              Store Program Policies, including the Limited Use requirements.{" "}
            </li>
            <li>
              <strong>Legal.</strong> We may use your information to protect our
              legal rights and comply with our legal obligations.
            </li>
            <li>
              <strong>Influencers.</strong> We may use your information in
              connection with the facilitation of services and communications
              between you and participating Influencers.
            </li>
            <li>
              <strong>Customer Accounts.</strong> We may use your information to
              create and administer customer accounts and personalize your
              experience.
            </li>
            <li>
              <strong> Provide Support and Respond to Inquiries.</strong> We may
              use your information to provide support, respond to your
              inquiries, and notify you about changes to our services, Software,
              or Website.
            </li>
            <li>
              <strong>Confirm Identity.</strong> We may use your personal
              information to confirm your identity.
            </li>
          </ul>
        </div>
        <Typography variant={"h6"} fontWeight={"bold"}>
          How We Disclose Collected Information
        </Typography>
        <Typography variant={"body1"} fontWeight={"bold"}>
          Personal Information
        </Typography>
        <div className={"list-body"}>
          <Typography variant={"body1"}>
            We do not sell, rent, trade, or publish your personal information to
            any non-affiliated parties. The personal information collected
            through the Website and Software is used solely to deliver our
            services. We will only disclose your personal information as
            follows:
          </Typography>
          <ul className={"list"}>
            <li>
              <strong>Service Providers.</strong> We disclose your information
              with service providers who work on our behalf to assist us in
              providing our services, including credit card processors and email
              service providers. Our service providers are contractually
              obligated to comply with this Policy and do not have any rights to
              use your information, beyond what is necessary to assist us in the
              performance of our services.
            </li>
            <li>
              <strong>Legal.</strong> We may disclose your information if we are
              legally required to or if we believe that it is necessary to
              comply with a law, regulation, or valid legal procedure, such as a
              subpoena or court order.
            </li>
            <li>
              <strong>Merger.</strong> We may disclose your information if we
              are merged with/acquired by another entity.
            </li>
            <li>
              <strong>Protection.</strong> We may disclose your information if
              we believe it would be reasonably necessary to prevent imminent
              and serious bodily harm to a human being, to investigate
              fraudulent activity or breaches of Website or Software security,
              or to protect our rights or property.
            </li>
          </ul>
        </div>
        <Typography variant={"body1"} fontWeight={"bold"}>
          Non-Personal Information
        </Typography>
        <Typography variant={"body1"}>
          We may disclose de-identified information (i.e., information that
          cannot identify you) to help us improve our Website, Software, and
          services.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Data Security
        </Typography>
        <Typography variant={"body1"}>
          Although no security system is impenetrable, we take appropriate
          technical and organizational measures to help secure your information.
          However, you are responsible for taking precautions to secure your
          customer account username and password, and you agree that any
          transactions that occur through your account will be your sole
          responsibility.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Data Retention
        </Typography>
        <Typography variant={"body1"}>
          We will retain your personal information for no longer than is
          necessary for the purposes it was originally collected, unless
          required by law to retain it longer. You may request deletion of your
          customer account at any time by contacting support.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Data Transfers
        </Typography>
        <Typography variant={"body1"}>
          We are a Florida-based company whose servers are housed in the United
          States. All information provided to us, and all information we
          collect, is transferred within the United States only, and is
          processed and stored according to United States law. Please note, upon
          your installation of the Software, your Metrics may be transferred to
          us, and as such, you are agreeing to such transfer of your Metrics to
          the United States.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Do Not Track
        </Typography>
        <Typography variant={"body1"}>
          We do not currently respond to “Do Not Track” signals or other similar
          opt-out mechanisms, other than those specifically set forth in this
          Policy, including general web browser “Do Not Track” settings.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Biometric Data Collection and Storage Policy
        </Typography>
        <Typography variant={"body1"}>
          This Biometric Data and Identity Verification Documents Collection and
          Storage Policy (the "Policy") last updated July 28, 2023, applies to
          your use of Blossm Media, LLC's ("we," "us," or "our") promotion
          buying and selling platform available through Blossm.com (the
          "Platform"). Please take the time to review before you proceed with
          submitting your Identity Verification Documents. As used herein
          "Identity Verification Documents" refers to all images you may submit
          to us either directly or through a third party intended to comply with
          our identity verification requirements which currently require a clear
          color image of your unexpired government-issued photo identification
          containing a recent and recognizable photo of you, together with a
          live burst/series of images taken of your 'Selfie'.
        </Typography>
        <Typography variant={"body1"}>
          We use technology from Incode Technologies, Inc. (“Incode”) for
          identity verification, fraud prevention, and security purposes,
          (“Services”). Company and/or Incode may collect and generate biometric
          identifiers and biometric information, such as your faceprint
          (“Biometric Data”), a selfie image, and, where applicable, information
          from your government-issued identification or your interactions with
          Incode and Company systems while onboarding (collectively with
          Biometric Data, “Personal Data”) for the Services, and may disclose
          such data to service providers or government entities as required.
          Learn more about how your Personal Data is processed, including
          retention, Company’s and/or Incode’s terms, including mandatory
          arbitration and class action waiver, and data processing practices and
          deletion in this Policy and
          <Link
            className={"privacy-page-link"}
            target="_blank"
            rel="noopener noreferrer"
            // sx={{ textDecoration: "underline" }}
            href={"https://incode.com/privacy-policy/"}
          >
            Incode Privacy Notice.
          </Link>
        </Typography>
        <Typography variant={"body1"}>
          By ticking the box consenting to this Policy during the account
          creation process, you accept providing us with your identification
          Personal Data, including your Biometric Information, for Company’s and
          Incode’s collection, storage, retention, use, transfer and disclosure
          of such information to provide the identity verification services,
          consistent with this Policy and Terms & Conditions which you may find
          at
          <Link
            className={"privacy-page-link"}
            target="_blank"
            rel="noopener noreferrer"
            href={"/terms"}
          >
            https://blossm.com/terms/
          </Link>
          . Even if you consent, you have the right to request the withdrawal of
          your consent at any time by deleting your account with the
          understanding that we may be required to retain certain information in
          connection with age verification and other obligations.
        </Typography>
        <Typography variant={"body1"}>
          If you do not wish to have your information used in line with this
          Policy and
          <Link
            className={"privacy-page-link"}
            target="_blank"
            rel="noopener noreferrer"
            href={"https://incode.com/privacy-policy/"}
          >
            Incode's Privacy Policy,
          </Link>
          you must not submit Identity Verification Documents.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          What is Biometric Data
        </Typography>
        <Typography variant={"body1"}>
          Biometric data is any biological characteristics of a person, or
          information based upon such a characteristic, including
          characteristics such as those defined as "biometric identifiers" and
          "biometric information" under the Illinois Biometric Information
          Privacy Act, 740 ILCS 14/1, et seq. "Biometric identifier" means a
          retina or iris scan, fingerprint, voiceprint, or scan of hand or face
          geometry. "Biometric information" means any information, regardless of
          how it is captured, converted, stored, or shared, based on an
          individual's biometric identifier used to identify an individual.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Biometric Data We May Collect and Process
        </Typography>
        <Typography variant={"body1"}>
          When you submit Identity Verification Documents either directly to us
          or through a secure third party, a three-dimensional scan of your face
          geometry will be taken. You will be prompted to take a short
          burst/series of images of your face using the camera on your mobile
          device or other internet connected webcam. We, directly or through a
          secure third party, may also collect a similar face geometry during
          your live video broadcasts on the Platform for comparison to the
          biometric data collected during submission of your Identity
          Verification Documents.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          Why is Biometric Data Collected?
        </Typography>
        <Typography variant={"body1"}>
          Your biometric data is collected and used for identity verification
          purposes in furtherance of our identity verification procedures and
          practices, which exist to prevent fraudulent and other unauthorized
          use of the Blossm platform. This information is used to further ensure
          that the individual providing the Identity Verification Documents is
          the person depicted in such documents and the person accessing the
          account(s) with which such documents are associated.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          How is Your Biometric Data Stored?
        </Typography>
        <Typography variant={"body1"}>
          The photo of your face from your Identity Verification Documents (the
          "Selfie") will be cropped and stored separately from your original
          Identity Verification Documents and made available through a secure,
          close-ended system to our team of reviewers to confirm that you are
          the person appearing through your account. Your Identity Verification
          Documents with your date of birth and real name will be stored
          separately in a secure system. Your Identity Verification Documents
          will only be reviewed by members of our team engaged in identity
          verification and routine audits of previously submitted identity
          verification. Any and all biometric data generated from your Identity
          Verification Documents (the "Biometric Data") is also stored securely
          and separately apart from your Selfie. The Biometric Data will only be
          used to verify your identification.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          How Long is Your Biometric Data Stored?
        </Typography>
        <Typography variant={"body1"}>
          Your Biometric Data will be retained for the same period as your
          Identity Verification Documents, where certain information may be
          retained in compliance with legal and other obligations. To the extent
          that such information is retained, it is retained solely to comply
          with relevant obligations and is not accessible by users.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          No Obligation to Accept Identity Verification Documents
        </Typography>
        <Typography variant={"body1"}>
          Notwithstanding anything herein to the contrary, we reserve the right
          to specify which government issued identification may be accepted
          based upon your location and our sole determination of the reliability
          of such documentation. As an example of documentation we do not
          accept, by way of example only and not limitation, we do not accept
          handwritten and/or paper documents.
        </Typography>
        <Typography variant={"body1"}>
          Additionally, as more fully detailed throughout the Platform,
          particularly during the identity verification process, the acceptable
          government-issued identification must contain a photograph of you that
          is both recent and recognizable. Again, by way of example only,
          identification containing photos of you as a young minor or photos
          from several years ago, regardless of your current age or age at the
          time the image was taken, may be rejected under this requirement.
          Additionally, by way of further example only, identification
          containing recent images which do not reflect alterations(such as
          surgeries, piercings, tattoos, or other modifications, whether
          temporary or permanent) in your face may also be rejected.
        </Typography>
        <Typography variant={"h6"} fontWeight={"bold"}>
          California Residents
        </Typography>
        <Typography variant={"body1"}>
          This section supplements the Policy and applies only to California
          residents. Pursuant to the California Consumer Privacy Act (“CCPA”),
          California residents have certain rights with respect to their
          personal information.
        </Typography>

        <Typography variant={"body1"}>
          The CCPA requires us to provide the categories of personal information
          (as defined in the CCPA) we collect, or have collected in the last 12
          months, they include:
        </Typography>

        <Typography variant={"body1"}>
          Identifiers (including name, username, email address, postal address,
          telephone number, and other similar identifiers)
        </Typography>
        <Typography variant={"body1"}>
          The categories of personal information listed in the California
          Customer Records statute (Cal. Civ. Code § 1798.80) (including name,
          address, telephone number, credit card number, debit card number, or
          any other financial information)
        </Typography>
        <Typography variant={"body1"}>
          Commercial information (including services purchased or considered,
          payment history, transaction history information).
        </Typography>
        <Typography variant={"body1"}>
          Pursuant to the CCPA, we do not “sell” personal information and have
          not sold any personal information in the past 12 months. As such,
          there is no need to opt-out of the sale of your personal information.
          We use and disclose the above categories of personal information
          consistent with the business purposes discussed throughout this
          Policy.
        </Typography>

        <Typography variant={"body1"}>
          Under the CCPA, as California residents, you may request:
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The categories of personal information we have collected or disclosed
          about you
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The categories of sources of such personal information
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The business or commercial purpose for which we have collected such
          personal information and the categories of third parties with whom we
          disclose such personal information
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The actual personal information we have collected about you
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          That we delete personal information that we have collected about you
          and,
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          To opt-out of the sale of personal information, and whether we have
          sold your personal information to any third parties and for what
          purpose
        </Typography>
        <Typography variant={"body1"}>
          We will not discriminate against you for exercising any of these
          rights. However, certain information may be exempt from requests under
          the law.
        </Typography>

        <Typography variant={"body1"}>
          The foregoing requests pursuant to the CCPA can only be made 2 times
          within a 12-month period at no charge. We reserve the right to deny or
          charge a reasonable fee with respect to meeting additional requests
          during any such 12-month period. Moreover, any information we provide
          per such request will only include information that we have collected
          over the previous 12 months from the date of such request.
        </Typography>

        <Typography variant={"body1"}>
          For your protection, we will need to verify your identity before
          complying with your request. Accordingly, you may need to provide us
          with personal information, such as your name and email address.
          However, if there is no personal information that we can verify, we
          may be unable to complete your request. We will attempt to respond to
          all verifiable requests within 45 days of receipt of such requests.
          Please note, to designate an authorized agent to make such requests on
          your behalf, in addition to the foregoing, you will need to provide a
          written authorization or power of attorney signed by you.
        </Typography>

        <Typography variant={"body1"}>
          If you are a California resident and would like to exercise any of the
          above rights pursuant to the CCPA, please contact us via email at
          privacy@blossm.com.
        </Typography>

        <Typography variant={"h6"} fontWeight={"bold"}>
          European Union Residents
        </Typography>
        <Typography variant={"body1"}>
          This section supplements the Policy and applies only to European Union
          (“EU”) residents. Pursuant to the General Data Protection Regulation
          (“GDPR”), EU residents that visit or access the Website or Software
          from the EU have certain rights with respect to their “personal data,”
          as such term is defined in the GDPR.
        </Typography>

        <Typography variant={"body1"}>
          Under the GDPR, as an EU resident, you may have the following rights:
        </Typography>

        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to obtain access to your personal data
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to update or erase inaccurate or incomplete data
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to erase your personal data, subject to limitations under
          the law
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to restrict the processing of your personal data
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to transfer or copy your personal data
        </Typography>
        <Typography variant={"body1"} sx={{ ml: 2 }}>
          The right to withdraw your consent to the processing of your personal
          data for certain purposes
        </Typography>
        <Typography variant={"body1"}>
          If you are an EU resident and would like to exercise your rights
          pursuant to the GDPR, please contact us via email at
          privacy@blossm.com. We may ask that you verify your identity prior to
          responding to any of your requests. If there is no verifiable personal
          information, we may be unable to complete any such requests.
        </Typography>

        <Typography variant={"h6"} fontWeight={"bold"}>
          Changes to the Policy
        </Typography>
        <Typography variant={"body1"}>
          We may change the Policy from time to time. If we make any changes to
          the Policy, we will change the “Last Updated” date at the top portion
          of this page. We encourage you to visit this page from time to time
          for the latest developments on our privacy practices. Your continued
          use of our Website and Software signifies your acceptance of any
          changes to the Policy. If you do not agree with such changes, do not
          use the Website or Software.
        </Typography>

        <Typography variant={"h6"} fontWeight={"bold"}>
          Contact Information
        </Typography>
        <Typography variant={"body1"}>
          We welcome your comments or questions regarding this Policy. Please
          feel free to e-mail us at privacy@blossm.com.
        </Typography>
      </div>
    </StyledPrivacy>
  )
}

const StyledPrivacy = styled.div<{
  theme: Theme
  isMobile: boolean
  isTablet: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  .app-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
  }

  .privacy-header {
    background: ${(props) => props.theme.palette.primary.main};
    padding-left: 16px;
    padding-bottom: 10px;
    padding-top: 10px;
    .header-icon {
      height: 25px;
      width: 100px;
      cursor: pointer;
    }
  }

  .text-float {
    box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2),
      0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12);
    padding-left: ${(props) =>
      props.isTablet ? "7vw" : props.isMobile ? "4vw" : "8vw"};
    background: ${Colors.white};
  }

  .list-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .privacy-body {
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-top: 120px;
    padding-bottom: ${(props) =>
      props.isTablet ? "3vh" : props.isMobile ? "2vh" : "2vh"};
    padding-left: ${(props) =>
      props.isTablet ? "7vw" : props.isMobile ? "4vw" : "8vw"};
    padding-right: ${(props) =>
      props.isTablet ? "7vw" : props.isMobile ? "4vw" : "8vw"};
  }

  .privacy-page-link {
    text-decoration: underline;
    margin-left: 4px;
    margin-right: 4px;
  }
`
